/**
 * Ouverture d'un onglet Fan dans le groupe lié à un ConversationsPanel —
 * mutations de layout PURES (aucun React ici).
 *
 * Extrait de `ConversationsPanel.tsx` pour être appelable depuis AILLEURS que
 * la grille : la table des transactions d'une créatrice a besoin d'ouvrir la
 * conversation d'un fan, et elle doit atterrir dans LE panneau Conversations
 * existant — pas dans un panneau parallèle qui donnerait deux vues du même
 * travail (demande user 2026-08-09).
 *
 * Comportement (inchangé depuis l'extraction) :
 *   - Click sur un fan depuis le ConversationsPanel A → ouvre un onglet Fan
 *     dans LE groupe lié à A.
 *   - Si un onglet pour CE fan existe déjà dans le groupe lié à A → l'activer
 *     (pas de duplicata).
 *   - Si un groupe lié à A existe déjà mais sans ce fan → ajouter un onglet.
 *   - Sinon → créer le groupe par split-edge à droite du groupe de A.
 *
 * « Lié à A » = chaque onglet fan porte `params.sourceConversationsId = A.id`.
 * On groupe les onglets par cette signature en cherchant dans le layout.
 *
 * Résultat : 2 panneaux Conversations indépendants peuvent chacun avoir leur
 * propre groupe d'onglets Fan. Cliquer 3 fans dans A → 3 onglets dans le groupe
 * Fan-de-A. Cliquer 2 fans dans B → 2 onglets dans un AUTRE groupe Fan-de-B.
 */
import { isMobileSync } from "@/core/hooks";
import {
  addPanel,
  listAllPanelInstances,
  removePanel,
  setActiveTab,
  type AddTarget,
  type Layout,
  type LayoutNode,
  type PanelInstance,
  type Split as SplitNode,
  type TabGroup as TabGroupNode,
} from "@/shell/layout";

export const CONVERSATIONS_PANEL_ID = "conversations";
export const FAN_PANEL_ID = "fan";
export const FAN_INFO_PANEL_ID = "fan-info";
/** 🤖 La colonne de l'agent, à droite d'Infos, au poste de chatteur. */
export const FRANCIS_PANEL_ID = "francis";

/**
 * Proportions par defaut du layout "conversation ouverte" sur desktop.
 * Cf demande user 2026-06-23 : le panneau chat doit etre TOUJOURS plus large
 * que le fan-info (un peu plus petit), et la liste de gauche peut se contenter
 * de 2 (voire 1) colonnes de cartes sur petit ecran.
 *
 * Le layout est cree par deux split-edge imbriques que `cleanupNode` aplatit
 * ensuite en un split a 3 colonnes (cf normalize.ts). Les parts finales sont
 * donc multiplicatives :
 *   - liste  = FAN_LAYOUT_LIST_PCT                       (~33 %)
 *   - chat   = FAN_LAYOUT_REST_PCT * FAN_CHAT_INNER/100  (~47 %)
 *   - info   = FAN_LAYOUT_REST_PCT * FAN_INFO_INNER/100  (~20 %)
 * Ces tailles ne s'appliquent qu'a la PREMIERE ouverture (creation du split) ;
 * un redimensionnement manuel de l'utilisateur est ensuite persiste et respecte.
 */
const FAN_LAYOUT_LIST_PCT = 33;
const FAN_LAYOUT_REST_PCT = 67;
const FAN_CHAT_INNER_PCT = 70;
const FAN_INFO_INNER_PCT = 30;

/**
 * 🎯 CINQ onglets fan au poste de chatteur — la seule limite en dur, et c'est
 * Kayla qui l'a nommée (09-09-2026 : « je vois qu'ils s'emmêlent les pinceaux
 * avec tous ces onglets, limite-les à cinq »). Au sixième, le plus ancien
 * sans non lus se ferme tout seul (sa décision, même jour) ; s'ils ont tous
 * des non lus, le plus ancien quand même — refuser d'ouvrir aurait été
 * l'autre option, elle ne l'a pas prise.
 */
export const ONGLETS_FAN_MAX_AU_POSTE = 5;

/**
 * 📐 La disposition FIGÉE du poste de chatteur : liste · discussion · info,
 * aux parts du poste. Kayla, 09-09-2026 : « la possibilité d'écarter
 * les panneaux, l'enlever, leur donner une taille adaptée à leur écran ».
 *
 * Rend les trois parts pour un split à trois colonnes dont chaque groupe
 * porte, dans l'ordre, la liste, des onglets fan, et le panneau info ; `null`
 * pour tout autre split (il garde ses tailles, sans poignée).
 *
 * 📏 LES PARTS DU POSTE — mesurées sur Fanvue (Kayla, 10-09-2026 : « je veux
 * en arriver à avoir des dimensions pareilles »), dans son Chrome, à 1920 px :
 * liste 398, discussion 572, outils 348, agent 420 — soit 22 · 31 · 19 · 23 %
 * de la zone utile. La liste passe donc de 33 à 22 % : c'est elle qui faisait
 * des cartes énormes. La discussion prend le reste tant que le panneau
 * « Francis » (l'agent, à droite) n'existe pas ; c'est à LUI qu'on prendra
 * ~23 % le jour où il arrive, pas à la liste. Les chatteurs sont sur de
 * petits portables (1366 px) : à 22 %, la liste fait ~300 px, la largeur pour
 * laquelle `LigneDeLaPile` a été dessinée.
 */
const LARGEURS_DU_POSTE: readonly [number, number, number] = [22, 46, 32];

export function taillesFigeesDuPoste(split: SplitNode): number[] | null {
  if (split.direction !== "row") return null;
  const porte = (n: LayoutNode | undefined, panelId: string) =>
    n?.kind === "tabs" && n.panels.some((p) => p.panelId === panelId);
  const [liste, fan, info] = split.children;
  if (
    !porte(liste, CONVERSATIONS_PANEL_ID) ||
    !porte(fan, FAN_PANEL_ID) ||
    !porte(info, FAN_INFO_PANEL_ID)
  ) {
    return null;
  }
  if (split.children.length === 3) return [...LARGEURS_DU_POSTE];
  return null;
}

/**
 * 🪦 Francis a été une QUATRIÈME colonne, à droite d'Infos, du 10-09 au
 * 17-09-2026 (parts de Fanvue : 22 · 33 · 22 · 23). Kayla, devant un chatteur
 * sur un portable : « mets-toi à la place d'un chatteur qui a un petit écran,
 * c'est ingérable […] au lieu de faire deux volets, tout dans le même
 * panneau ». Il vit depuis dans l'onglet « Francis » du panneau de droite
 * (`InfoViewDuPoste`). Une colonne qui traîne dans une disposition
 * enregistrée d'avant est retirée à l'ouverture d'un fan : au poste, ce serait
 * Francis deux fois, et 23 % de l'écran en moins.
 */
export function sansLaColonneFrancis(layout: Layout): Layout {
  return listAllPanelInstances(layout)
    .filter((p) => p.panelId === FRANCIS_PANEL_ID)
    .reduce((l, p) => removePanel(l, p.id), layout);
}

export interface OpenFanArgs {
  conversationId: string;
  /** ID d'instance du ConversationsPanel d'ou vient le click (lien). */
  sourceConversationsId: string;
  /** Titre humain du tab (nom du fan). */
  title: string;
  /**
   * 🎯 La borne du poste de chatteur. Absente ⇒ aucune limite (admin,
   * managers). Présente ⇒ au-delà de `max` onglets fan dans le groupe lié, le
   * plus ancien sans non lus se ferme pour faire place ; `nonLus` répond pour
   * une conversation (l'appelant lit `unread_count` là où il est).
   */
  limite?: { max: number; nonLus: (conversationId: string) => number };
  /** 🗂️ Au poste, Francis est un onglet du panneau de droite : une colonne
   *  `francis` d'une disposition d'avant est retirée. Absent ⇒ on ne touche
   *  à rien (admin en Cartes, qui peut l'ouvrir où il veut). */
  sansFrancis?: boolean;
}

export interface OpenFanResult {
  layout: Layout;
  /** ID d'instance du tab Fan (nouveau ou existant). Null si pas trouvable
   *  (cas edge : source disparue entre 2 frames). Utilise pour focus mobile
   *  via mobileActiveInstanceIdAtom. */
  instanceId: string | null;
}

/** Collecte les instanceIds de TOUS les onglets fan ouverts dans le layout
 *  (toutes TabGroups / splits confondus). Sert au fail-closed du gate pour
 *  fermer les conversations restées ouvertes quand l'accès est révoqué. */
export function collectAllFanInstanceIds(node: LayoutNode): string[] {
  if (node.kind === "tabs") {
    return node.panels
      .filter((p) => p.panelId === FAN_PANEL_ID)
      .map((p) => p.id);
  }
  const out: string[] = [];
  for (const child of node.children) {
    out.push(...collectAllFanInstanceIds(child));
  }
  return out;
}

/**
 * Le ConversationsPanel dans lequel une ouverture « venue d'ailleurs » doit
 * atterrir, ou `null` si aucun n'est ouvert.
 *
 * Priorité à celui qui héberge DÉJÀ des onglets fan : c'est l'espace de travail
 * courant de l'opérateur. Sans cette préférence, un second panneau Conversations
 * ouvert au fond d'un workspace capterait l'ouverture et la conversation
 * apparaîtrait loin du regard. À défaut, le premier rencontré en DFS.
 */
export function findConversationsHostId(layout: Layout): string | null {
  const instances = listAllPanelInstances(layout);
  const sources = new Set<string>();
  for (const p of instances) {
    if (p.panelId !== FAN_PANEL_ID) continue;
    const src = p.params?.["sourceConversationsId"];
    if (typeof src === "string") sources.add(src);
  }
  const hosts = instances.filter((p) => p.panelId === CONVERSATIONS_PANEL_ID);
  return hosts.find((p) => sources.has(p.id))?.id ?? hosts[0]?.id ?? null;
}

export function openFanInLinkedGroup(
  layout: Layout,
  args: OpenFanArgs,
): OpenFanResult {
  const res = ouvrirLeFan(layout, args);
  if (!args.sansFrancis) return res;
  return { ...res, layout: sansLaColonneFrancis(res.layout) };
}

function ouvrirLeFan(layout: Layout, args: OpenFanArgs): OpenFanResult {
  const { conversationId, sourceConversationsId, title, limite } = args;

  // 1) Cherche un tab fan deja ouvert pour CE fan + CETTE source.
  const existing = findFanInstance(
    layout.root,
    conversationId,
    sourceConversationsId,
  );
  if (existing) {
    const group = findGroupContaining(layout.root, existing.id);
    if (!group) return { layout, instanceId: existing.id };
    const idx = group.panels.findIndex((p) => p.id === existing.id);
    if (idx >= 0 && group.activeIndex !== idx) {
      return {
        layout: setActiveTab(layout, group.id, idx),
        instanceId: existing.id,
      };
    }
    return { layout, instanceId: existing.id };
  }

  // 2) Cherche un group qui contient deja des tabs Fan pour CETTE source —
  //    on y ajoute le nouveau tab (group "Fans-de-A" deja cree).
  const linkedGroup = findGroupHostingFanFromSource(
    layout.root,
    sourceConversationsId,
  );
  if (linkedGroup) {
    const aFermer = limite ? ongletAFermer(linkedGroup, limite) : null;
    // `removePanel` peut réécrire les ids de groupe en normalisant : on
    // retrouve le groupe lié APRÈS, jamais on ne réutilise l'ancien.
    const base = aFermer ? removePanel(layout, aFermer) : layout;
    const groupe = aFermer
      ? findGroupHostingFanFromSource(base.root, sourceConversationsId)
      : linkedGroup;
    const res = addPanel(
      base,
      FAN_PANEL_ID,
      groupe ? { kind: "group", groupId: groupe.id } : { kind: "active-group" },
      {
        params: { conversationId, sourceConversationsId },
        title,
      },
    );
    return { layout: res.layout, instanceId: res.instanceId };
  }

  // 3) Pas de groupe lie — split-edge a droite du group du source.
  //    En mode mobile, on n'a qu'un seul panneau visible : le split-edge
  //    est coerce vers active-group pour que le fan s'ouvre comme un nouvel
  //    onglet du panneau courant (cf demande user : "ouvrir une conversation
  //    ouvrirait un nouvel onglet, pas un nouveau panneau").
  //
  //    Sur desktop, on cree EN PLUS un panneau `fan-info` detache a droite
  //    du nouveau fan group (cf demande user 2026-06-20 : "le truc info
  //    qui soit decolle et mis a droite par defaut"). L'user peut toujours
  //    le re-attacher inline / fermer ; on ne re-detache plus pour ce
  //    fan group une fois cree (l'auto-detach n'a lieu qu'a la creation).
  const sourceGroup = findGroupContaining(layout.root, sourceConversationsId);
  if (sourceGroup) {
    const onMobile = isMobileSync();
    const target: AddTarget = onMobile
      ? { kind: "active-group" }
      : {
          kind: "split-edge",
          targetNodeId: sourceGroup.id,
          edge: "right",
          // liste (gauche) plus etroite que le reste (chat + info)
          sizes: [FAN_LAYOUT_LIST_PCT, FAN_LAYOUT_REST_PCT],
        };
    const res = addPanel(layout, FAN_PANEL_ID, target, {
      params: { conversationId, sourceConversationsId },
      title,
    });
    if (!onMobile) {
      const fanGroup = findGroupContaining(res.layout.root, res.instanceId);
      if (fanGroup) {
        const withInfo = addPanel(
          res.layout,
          FAN_INFO_PANEL_ID,
          {
            kind: "split-edge",
            targetNodeId: fanGroup.id,
            edge: "right",
            // chat plus large que fan-info
            sizes: [FAN_CHAT_INNER_PCT, FAN_INFO_INNER_PCT],
          },
          { params: { sourceFanGroupId: fanGroup.id } },
        );
        return { layout: withInfo.layout, instanceId: res.instanceId };
      }
    }
    return { layout: res.layout, instanceId: res.instanceId };
  }

  // 4) Fallback (cas edge : source introuvable).
  const res = addPanel(
    layout,
    FAN_PANEL_ID,
    { kind: "active-group" },
    {
      params: { conversationId, sourceConversationsId },
      title,
    },
  );
  return { layout: res.layout, instanceId: res.instanceId };
}

/**
 * L'onglet fan qui doit se fermer pour qu'un nouveau entre, ou `null` s'il
 * reste de la place. Les onglets sont dans l'ordre d'ouverture (`addPanel`
 * ajoute en queue) : le plus ancien est le premier. On préfère un onglet sans
 * non lus ; à défaut, le plus ancien tout court.
 */
export function ongletAFermer(
  group: TabGroupNode,
  limite: { max: number; nonLus: (conversationId: string) => number },
): string | null {
  const fans = group.panels.filter((p) => p.panelId === FAN_PANEL_ID);
  if (fans.length < limite.max) return null;
  const sansNonLus = fans.find((p) => {
    const id = p.params?.["conversationId"];
    return typeof id === "string" && limite.nonLus(id) === 0;
  });
  return (sansNonLus ?? fans[0])?.id ?? null;
}

export function findFanInstance(
  node: LayoutNode,
  conversationId: string,
  sourceConversationsId: string,
): PanelInstance | null {
  if (node.kind === "tabs") {
    return (
      node.panels.find(
        (p) =>
          p.panelId === FAN_PANEL_ID &&
          p.params?.["conversationId"] === conversationId &&
          p.params?.["sourceConversationsId"] === sourceConversationsId,
      ) ?? null
    );
  }
  for (const child of node.children) {
    const found = findFanInstance(child, conversationId, sourceConversationsId);
    if (found) return found;
  }
  return null;
}

function findGroupHostingFanFromSource(
  node: LayoutNode,
  sourceConversationsId: string,
): TabGroupNode | null {
  if (node.kind === "tabs") {
    return node.panels.some(
      (p) =>
        p.panelId === FAN_PANEL_ID &&
        p.params?.["sourceConversationsId"] === sourceConversationsId,
    )
      ? node
      : null;
  }
  for (const child of node.children) {
    const found = findGroupHostingFanFromSource(child, sourceConversationsId);
    if (found) return found;
  }
  return null;
}

function findGroupContaining(
  node: LayoutNode,
  instanceId: string,
): TabGroupNode | null {
  if (node.kind === "tabs") {
    return node.panels.some((p) => p.id === instanceId) ? node : null;
  }
  for (const child of node.children) {
    const found = findGroupContaining(child, instanceId);
    if (found) return found;
  }
  return null;
}
